import { Code, FileText } from 'lucide-react'
import { useState } from 'react'
import Markdown from 'react-markdown'
import rehypeKatex from 'rehype-katex'
import remarkBreaks from 'remark-breaks'
import remarkGfm from 'remark-gfm'
import remarkMath from 'remark-math'

import { Button } from '@components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@components/ui/card'
import { Input } from '@components/ui/input'
import { Label } from '@components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@components/ui/tabs'
import { Textarea } from '@components/ui/textarea'
import {preprocessLaTeX} from "@lib/utils.ts";

export function ScratchPage() {
  const [markdownText, setMarkdownText] = useState(`# Scratch Page

## Math Examples

Inline math: $E = mc^2$

\\[\\text{Net Profit} = \\text{Final Value of Investment} - \\text{Cost of Investment}\\]

Block math:
$$\\int_{-\\infty}^{\\infty} e^{-x^2} dx = \\sqrt{\\pi}$$
`)

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">🛠️ Scratch Page</h1>
        <p className="text-muted-foreground">
          A testing playground for components, markdown, and other features
        </p>
      </div>

      <Tabs defaultValue="markdown" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="markdown" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Markdown
          </TabsTrigger>
          <TabsTrigger value="components" className="flex items-center gap-2">
            <Code className="h-4 w-4" />
            Components
          </TabsTrigger>
        </TabsList>

        <TabsContent value="markdown" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Markdown Editor</CardTitle>
                <CardDescription>Edit markdown content here</CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={markdownText}
                  onChange={(e) => setMarkdownText(e.target.value)}
                  className="min-h-[400px] font-mono"
                  placeholder="Type your markdown here..."
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Live Preview</CardTitle>
                <CardDescription>See how your markdown renders</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="prose prose-sm dark:prose-invert max-w-none min-h-[400px] overflow-y-auto">
                  <Markdown
                    remarkPlugins={[remarkBreaks, remarkGfm, remarkMath]}
                    rehypePlugins={[rehypeKatex]}
                  >
                    {preprocessLaTeX(markdownText)}
                  </Markdown>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="components" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Button Variations</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  <Button>Default</Button>
                  <Button variant="destructive">Destructive</Button>
                  <Button variant="outline">Outline</Button>
                  <Button variant="secondary">Secondary</Button>
                  <Button variant="ghost">Ghost</Button>
                  <Button variant="link">Link</Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Input Fields</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="test-input">Test Input</Label>
                  <Input id="test-input" placeholder="Enter some text..." />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="test-textarea">Test Textarea</Label>
                  <Textarea id="test-textarea" placeholder="Enter multiline text..." />
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}